/**
 * Sprint P32 · Cockpit viewport toolbar (top ribbon).
 * Fit-mode + View + Snapshot + Share buttons with dropdowns.
 */
import { useEffect, useState } from "react";
import {
  ArrowLeft, Cube, Compass, ArrowsOutCardinal, Camera, Share, Check,
  CaretDown, FrameCorners, Images,
} from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";


export const FIT_MODES = [
  { key: "active",   label: "Fit Active Model",  desc: "auto — blocks + nearby holes" },
  { key: "all",      label: "Fit All Data",      desc: "every visible layer" },
  { key: "ore",      label: "Fit Ore",           desc: "blocks ≥ reporting cut-off" },
  { key: "pit",      label: "Fit Pit",           desc: "engineering open-pit" },
  { key: "selected", label: "Fit Selected",      desc: "clicked object" },
];
export const VIEW_MODES = [
  { key: "iso_ne", label: "Iso NE" },
  { key: "iso_sw", label: "Iso SW" },
  { key: "top",    label: "Top" },
  { key: "front",  label: "Front (N-S)" },
  { key: "east",   label: "East (E-W)" },
  { key: "north",  label: "North" },
];


export function ViewportToolbar({
  project, onExitCockpit, onFitMode, onViewChange, onManualCamera, onReset,
  onSnapshot, onShare, snapshotBusy, shareStatus,
  onCaptureView, captureViewBusy,
  onOpenGallery, galleryOpen,
  fitBusy, fitError,
}) {
  const [openFit, setOpenFit] = useState(false);
  const [openView, setOpenView] = useState(false);

  useEffect(() => {
    const close = () => { setOpenFit(false); setOpenView(false); };
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, []);

  return (
    <div className="bg-slate-950 border-b border-slate-800 flex items-stretch text-slate-200"
         data-testid="cockpit-ribbon">
      <button onClick={onExitCockpit}
              className="px-3 flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-slate-400 hover:bg-slate-800 hover:text-white border-r border-slate-800"
              data-testid="cockpit-back">
        <ArrowLeft size={12} /> Wizard
      </button>
      <div className="px-4 flex items-center gap-2 border-r border-slate-800">
        <Cube size={16} className="text-sky-400" weight="fill" />
        <div className="text-[13px] font-bold text-slate-100">{project?.name || "Project"}</div>
        <Badge className="bg-sky-500/20 text-sky-300 border-sky-500/30 text-[9px]">
          {project?.commodity || "—"}
        </Badge>
      </div>

      <div className="flex items-center gap-1 px-2 border-r border-slate-800">
        <div className="relative" onClick={(e) => e.stopPropagation()}>
          <RibbonBtn Icon={ArrowsOutCardinal}
                      label={fitBusy ? "Fitting…" : "Fit"}
                      onClick={() => { setOpenFit((v) => !v); setOpenView(false); }}
                      testId="cockpit-fit"
                      danger={!!fitError}
                      chevron />
          {openFit && (
            <div className="absolute top-full left-0 mt-1 z-50 min-w-[220px] bg-slate-900 border border-slate-700 rounded shadow-2xl py-1 text-slate-100"
                 data-testid="cockpit-fit-menu">
              <div className="px-3 py-1 text-[9px] font-mono uppercase tracking-widest text-slate-500 border-b border-slate-800">
                Fit camera to…
              </div>
              {FIT_MODES.map((m) => (
                <button key={m.key}
                        onClick={() => { onFitMode(m.key); setOpenFit(false); }}
                        data-testid={`cockpit-fit-${m.key}`}
                        className="w-full text-left px-3 py-1.5 hover:bg-slate-800 flex flex-col">
                  <span className="text-[12px] text-slate-100 font-medium">{m.label}</span>
                  <span className="text-[10px] text-slate-500">{m.desc}</span>
                </button>
              ))}
              <div className="border-t border-slate-800 mt-1 pt-1">
                <button onClick={() => { onManualCamera(); setOpenFit(false); }}
                        data-testid="cockpit-fit-manual"
                        className="w-full text-left px-3 py-1.5 hover:bg-slate-800 text-[12px] text-slate-200">
                  Manual — free camera
                </button>
                <button onClick={() => { onReset(); setOpenFit(false); }}
                        data-testid="cockpit-fit-reset"
                        className="w-full text-left px-3 py-1.5 hover:bg-slate-800 text-[12px] text-slate-200">
                  Reset camera
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="relative" onClick={(e) => e.stopPropagation()}>
          <RibbonBtn Icon={Compass} label="View" testId="cockpit-view"
                      onClick={() => { setOpenView((v) => !v); setOpenFit(false); }}
                      chevron />
          {openView && (
            <div className="absolute top-full left-0 mt-1 z-50 min-w-[160px] bg-slate-900 border border-slate-700 rounded shadow-2xl py-1 text-slate-100"
                 data-testid="cockpit-view-menu">
              {VIEW_MODES.map((v) => (
                <button key={v.key}
                        onClick={() => { onViewChange(v.key); setOpenView(false); }}
                        data-testid={`cockpit-view-${v.key}`}
                        className="w-full text-left px-3 py-1.5 hover:bg-slate-800 text-[12px] text-slate-200">
                  {v.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <RibbonBtn Icon={Camera}
                   label={snapshotBusy ? "Saving…" : "Snapshot"}
                   onClick={onSnapshot} testId="cockpit-snapshot" />
        {/* Iter98c · Capture view into the CPR "3D Model Views"
            appendix (Appendix Y) library. Distinct from the cover-page
            snapshot above — this appears in Appendix Y with the current
            camera pose and colour mode metadata. */}
        <RibbonBtn Icon={FrameCorners}
                   label={captureViewBusy ? "Capturing…" : "Capture View"}
                   onClick={onCaptureView} testId="cockpit-capture-view" />
        <RibbonBtn Icon={Images}
                   label={galleryOpen ? "Hide Gallery" : "Gallery"}
                   onClick={onOpenGallery} testId="cockpit-gallery-toggle" />
        <RibbonBtn Icon={shareStatus === "copied" ? Check : Share}
                   label={shareStatus === "copied" ? "Copied" : "Share"}
                   onClick={onShare} testId="cockpit-share" />
      </div>
      <div className="flex-1" />
    </div>
  );
}


export function RibbonBtn({ Icon, label, onClick, testId, chevron, danger, active }) {
  return (
    <button onClick={onClick} data-testid={testId}
            className={`flex flex-col items-center gap-0.5 px-2 py-1.5 rounded transition ${
              danger
                ? "text-rose-400 hover:bg-rose-900/40"
                : active
                ? "text-sky-300 bg-sky-500/10"
                : "text-slate-300 hover:bg-sky-500/10 hover:text-sky-300"
            }`}>
      <div className="flex items-center gap-1">
        <Icon size={18} weight="duotone" />
        {chevron && <CaretDown size={9} />}
      </div>
      <div className="text-[9.5px] font-mono uppercase tracking-widest">{label}</div>
    </button>
  );
}
